import { useDeferredValue, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { PoolCell, PoolResponse, ShortLink } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { number } from "../lib/format";
import { PageHeader } from "../components/app-shell";
import { Button, Dialog, Empty, ErrorState, Field, Loading, Tag, useToast } from "../components/ui";

function NumericPoolCell({ cell, onAssign }: { cell: PoolCell; onAssign: () => void }) {
  const title = cell.availableAt
    ? `可用时间：${new Date(cell.availableAt).toLocaleString("zh-CN")}`
    : cell.targetUrl ?? cell.state;
  if (cell.state === "available") {
    return <button type="button" className="pool-cell pool-cell--available" title={title} onClick={onAssign}><span>{cell.slug}</span><ArrowRight size={12} /></button>;
  }
  if (cell.state === "occupied" && cell.shortUrl) {
    return (
      <a className="pool-cell pool-cell--occupied" href={cell.shortUrl} target="_blank" rel="noreferrer" title={title}>
        <span>{cell.slug}</span>
        <small>{cell.shortUrl.replace(/^https?:\/\//, "")}</small>
        <em>→ {cell.targetHost ?? "待审核"}</em>
      </a>
    );
  }
  return <div className="pool-cell pool-cell--cooldown" title={title}><span>{cell.slug}</span><small>冷却中</small></div>;
}

export function PoolPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const [mappingOpen, setMappingOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [slug, setSlug] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [note, setNote] = useState("");

  const query = useQuery({
    queryKey: ["pool", page, deferredSearch],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: "36" });
      if (deferredSearch) params.set("q", deferredSearch);
      return api<PoolResponse>(`/api/pool?${params.toString()}`);
    },
  });

  const closeMapping = () => {
    setMappingOpen(false);
    setEditingSlug(null);
    setSlug("");
    setTargetUrl("");
    setNote("");
  };

  const mappingMutation = useMutation({
    mutationFn: () => api<ShortLink>("/api/pool/entries", {
      method: "POST",
      ...jsonBody({ slug: slug.trim(), targetUrl: targetUrl.trim(), note }),
    }),
    async onSuccess(link) {
      toast.push(`映射已生效：/${link.slug} → ${link.targetHost}`);
      closeMapping();
      setPage(1);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pool"] }),
        queryClient.invalidateQueries({ queryKey: ["links"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
    onError: toast.fromError,
  });

  const deleteMutation = useMutation({
    mutationFn: (value: string) => api<{ deleted: true }>(`/api/pool/entries/${encodeURIComponent(value)}`, { method: "DELETE" }),
    async onSuccess() {
      toast.push("自定义映射及对应短链已删除");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pool"] }),
        queryClient.invalidateQueries({ queryKey: ["links"] }),
      ]);
    },
    onError: toast.fromError,
  });

  const openEdit = (cell: PoolCell) => {
    setEditingSlug(cell.slug);
    setSlug(cell.slug);
    setTargetUrl(cell.targetUrl ?? "");
    setNote(cell.note);
    setMappingOpen(true);
  };

  const submitMapping = (event: FormEvent) => {
    event.preventDefault();
    mappingMutation.mutate();
  };

  return (
    <div className="page pool-page">
      <PageHeader
        eyebrow="SCARCE ADDRESS SPACE · 号池"
        title="极短号池"
        body="上方保留 1–99 数字号段；下方由管理员直接配置任意字母、数字或字母数字短号及其目标网址。"
        actions={<><Button variant="primary" onClick={() => setMappingOpen(true)}><Plus size={15} />新增自定义映射</Button><Button onClick={() => navigate("/console/links?create=1")}>管理全部短链</Button></>}
      />
      {query.isLoading ? <Loading label="读取号池" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : query.data ? (
        <>
          <section className="pool-summary">
            <div><span className="eyebrow">NUMERIC</span><strong>{query.data.start}–{query.data.end}</strong><small>管理员数字号段</small></div>
            <div><span className="eyebrow">CUSTOM MAPS</span><strong>{number(query.data.custom.total)}</strong><small>管理员自定义映射</small></div>
            <div><span className="eyebrow">NUMERIC AVAILABLE</span><strong>{number(query.data.available)}</strong><small>当前可分配数字短号</small></div>
            <div className="pool-summary__legend"><Tag tone="accent">占用</Tag><Tag tone="outline">冷却</Tag><Tag tone="neutral">可用</Tag></div>
          </section>

          <section className="pool-board">
            <header><span className="eyebrow">NUMERIC MAP · CLICK TO ASSIGN</span><p>点击空白数字直接进入创建；占用格显示当前短链与目标主机。</p></header>
            <div className="pool-grid">
              {query.data.cells.map((cell) => <NumericPoolCell key={cell.slug} cell={cell} onAssign={() => navigate(`/console/links?create=1&slug=${encodeURIComponent(cell.slug)}`)} />)}
            </div>
          </section>

          <section className="named-pool-board">
            <header className="named-pool-toolbar named-pool-toolbar--simple">
              <div><span className="eyebrow">CUSTOM MAPPINGS · {number(query.data.custom.total)}</span><h2>自定义短号映射</h2></div>
              <label className="search-field"><Search size={14} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="搜索短号或备注" /></label>
              <Button variant="primary" onClick={() => setMappingOpen(true)}><Plus size={14} />添加映射</Button>
            </header>
            {query.data.custom.items.length ? (
              <div className="named-pool-grid">
                {query.data.custom.items.map((cell) => (
                  <article key={cell.slug} className={`named-pool-card named-pool-card--${cell.state}`}>
                    <header><strong>/{cell.slug}</strong><Tag tone="vip">映射</Tag></header>
                    <div className="named-pool-card__route">
                      <a href={cell.shortUrl ?? "#"} target="_blank" rel="noreferrer">{cell.shortUrl?.replace(/^https?:\/\//, "") ?? "冷却中"}<ExternalLink size={12} /></a>
                      <span>→ {cell.targetUrl ?? (cell.state === "cooldown" ? "等待释放" : "目标已清理")}</span>
                    </div>
                    <footer><span>{cell.note || "管理员自定义映射"}</span><div className="row-actions"><button type="button" className="icon-button" title="编辑映射" onClick={() => openEdit(cell)}><Pencil size={12} /></button><button type="button" className="icon-button icon-button--danger" title="删除映射和短链" onClick={() => deleteMutation.mutate(cell.slug)}><Trash2 size={12} /></button></div></footer>
                  </article>
                ))}
              </div>
            ) : <Empty title="没有自定义映射" body="添加一个短号和目标网址后会立即生效。" />}
            <footer className="pagination"><span>第 {query.data.custom.pagination.page} / {query.data.custom.pagination.totalPages} 页 · 匹配 {query.data.custom.pagination.total} 项</span><div><Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>← 上一页</Button><Button disabled={page >= query.data.custom.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>下一页 →</Button></div></footer>
          </section>
        </>
      ) : null}

      <Dialog open={mappingOpen} onClose={closeMapping} title={editingSlug ? `编辑 /${editingSlug}` : "新增短号映射"} eyebrow="DIRECT MAPPING · ADMIN">
        <form className="pool-import" onSubmit={submitMapping}>
          <Field label="短链后缀 · SLUG" hint="可使用字母、数字、下划线或连字符；1–99 建议直接点击上方数字格。"><input className="input" value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="promo2026" readOnly={Boolean(editingSlug)} required /></Field>
          <Field label="目标网址 · TARGET URL"><input className="input" type="url" value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} placeholder="https://example.com/path" required /></Field>
          <Field label="备注 · NOTE"><input className="input" value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：活动页或长期入口" /></Field>
          <div className="dialog-actions"><Button type="button" onClick={closeMapping}>取消</Button><Button variant="primary" type="submit" disabled={mappingMutation.isPending}>{mappingMutation.isPending ? "保存中" : editingSlug ? "更新映射" : "创建并生效"}</Button></div>
        </form>
      </Dialog>
    </div>
  );
}
